import React from 'react';
import PropTypes from 'prop-types';

const FONT_OPTIONS = [
  'Futura',
  'Didact Gothic',
  'Questrial',
  'Arial',
  'Georgia',
  'Times New Roman',
  'Courier New',
];

function SliderControl({
  label,
  value,
  valueLabel,
  min,
  max,
  step,
  onChange,
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <label className="text-xs font-medium text-gray-700">{label}</label>
        <span className="text-[11px] tabular-nums text-gray-500">
          {valueLabel}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </div>
  );
}

function PresetDetailsEditor({
  selectedPreset,
  setSelectedPreset,
  pageLanguage,
  handleUpdatePreset,
  handleSaveToContentLayout,
  handlePreviewPreset,
  handleCreateNewPreset,
  handleResetPreset,
  updatingPreset,
  title = 'Design Preset',
  subtitle,
  updateLabel = 'Update Design Preset',
  showActions = true,
  showSaveAsNew = true,
  showDiscardChanges = true,
  allowTextVisibility = true,
  textVisibilityOverride,
  previewLabel = 'Apply style to page text',
  beforeActions,
}) {
  if (!selectedPreset) return null;

  const config = selectedPreset.config || {};
  const isBilingual = pageLanguage?.includes('_');
  const fontSizeValue = Number(config.fontSize) || 60;
  const lineHeightValue = Number(config.lineHeight) || 2;
  const textWidthValue = Number(config.textWidth) || 833;
  const separatorSpacing = Number(config.separatorSpacing) || 0;
  const separatorHeight = Number(config.separatorHeight) || 10;
  const paddingValue = Number(config.padding?.top) || 0;
  const fontStyleTop = config.fontStyle || 'normal';
  const fontStyleBottom = config.fontStyleSecondary || 'italic';
  const showDivider = Boolean(config.showDivider);
  const alignment = config.alignment || 'center';
  const textVisibility = textVisibilityOverride || config.textVisibility || 'both';
  const verticalAnchor = config.verticalAnchor || 'block';
  const placementMode = config.placementMode || 'automatic';

  const updateConfig = (updates) => {
    setSelectedPreset({
      ...selectedPreset,
      config: {
        ...config,
        ...updates,
      },
    });
  };

  const updateUniformPadding = (value) => {
    updateConfig({
      padding: {
        top: value,
        right: value,
        bottom: value,
        left: value,
      },
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 truncate max-w-[220px]">
            {subtitle || selectedPreset.name}
          </p>
        </div>
        {isBilingual && textVisibility === 'both' && (
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={showDivider}
              onChange={(e) => updateConfig({ showDivider: e.target.checked })}
              className="rounded"
            />
            Line between languages
          </label>
        )}
      </div>

      {isBilingual && allowTextVisibility && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Languages displayed
          </label>
          <select
            value={textVisibility}
            onChange={(e) => updateConfig({ textVisibility: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="both">Text 1 and Text 2</option>
            <option value="primary">Text 1 only</option>
            <option value="secondary">Text 2 only</option>
          </select>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {textVisibility !== 'secondary' && (
              <button
                type="button"
                onClick={() => updateConfig({ textVisibility: textVisibility === 'both' ? 'secondary' : 'both' })}
                className="rounded-full border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:border-red-200 hover:text-red-700"
                title="Remove Text 1 from this preset"
              >
                × Text 1
              </button>
            )}
            {textVisibility !== 'primary' && (
              <button
                type="button"
                onClick={() => updateConfig({ textVisibility: textVisibility === 'both' ? 'primary' : 'both' })}
                className="rounded-full border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:border-red-200 hover:text-red-700"
                title="Remove Text 2 from this preset"
              >
                × Text 2
              </button>
            )}
          </div>
        </div>
      )}

      <SliderControl
        label="Font Size"
        value={fontSizeValue}
        valueLabel={`${fontSizeValue}px`}
        min={30}
        max={180}
        step={1}
        onChange={(value) =>
          updateConfig({ fontSizeMode: 'fixed', fontSize: value })
        }
      />

      <SliderControl
        label="Text Width"
        value={textWidthValue}
        valueLabel={`${textWidthValue}px`}
        min={100}
        max={2200}
        step={10}
        onChange={(value) =>
          updateConfig({ textWidthMode: 'fixed', textWidth: value })
        }
      />

      {isBilingual && textVisibility === 'both' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Center text using
          </label>
          <select
            value={verticalAnchor}
            onChange={(e) =>
              updateConfig({ verticalAnchor: e.target.value, verticalOffset: 0 })
            }
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="block">Center full text block</option>
            <option value="divider">Center divider line</option>
          </select>
        </div>
      )}

      {subtitle === 'Main text' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Main text placement
          </label>
          <select
            value={placementMode}
            onChange={(e) => updateConfig({ placementMode: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="automatic">Automatic — center from current text</option>
            <option value="fixed">Fixed — keep the saved position</option>
          </select>
        </div>
      )}

      <SliderControl
        label="Line Spacing"
        value={lineHeightValue}
        valueLabel={lineHeightValue.toFixed(2)}
        min={1}
        max={3}
        step={0.05}
        onChange={(value) => updateConfig({ lineHeight: value })}
      />

      {isBilingual && textVisibility === 'both' && (
        <SliderControl
          label="Gap between languages"
          value={separatorSpacing}
          valueLabel={`${separatorSpacing.toFixed(2)}x`}
          min={0}
          max={6}
          step={0.25}
          onChange={(value) => updateConfig({ separatorSpacing: value })}
        />
      )}

      {isBilingual && showDivider && textVisibility === 'both' && (
        <SliderControl
          label="Line thickness"
          value={separatorHeight}
          valueLabel={`${separatorHeight}px`}
          min={1}
          max={30}
          step={1}
          onChange={(value) => updateConfig({ separatorHeight: value })}
        />
      )}

      <SliderControl
        label="White Padding"
        value={paddingValue}
        valueLabel={`${paddingValue}px`}
        min={0}
        max={80}
        step={1}
        onChange={updateUniformPadding}
      />

      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Alignment
          </label>
          <select
            value={alignment}
            onChange={(e) => updateConfig({ alignment: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
            <option value="justify">Justify</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Top Font
          </label>
          <select
            value={config.fontFamily || 'Futura'}
            onChange={(e) => updateConfig({ fontFamily: e.target.value })}
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Bottom Font
          </label>
          <select
            value={config.fontFamilySecondary || config.fontFamily || 'Futura'}
            onChange={(e) =>
              updateConfig({ fontFamilySecondary: e.target.value })
            }
            className="w-full border rounded px-2 py-1.5 text-xs bg-white"
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={fontStyleTop.includes('italic')}
            onChange={(e) =>
              updateConfig({ fontStyle: e.target.checked ? 'italic' : 'normal' })
            }
            className="rounded"
          />
          Top italic
        </label>

        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={fontStyleBottom.includes('italic')}
            onChange={(e) =>
              updateConfig({
                fontStyleSecondary: e.target.checked ? 'italic' : 'normal',
              })
            }
            className="rounded"
          />
          Bottom italic
        </label>
      </div>

      <select
        value={config.backgroundMode ?? 'text'}
        onChange={(e) => updateConfig({ backgroundMode: e.target.value })}
        className="w-full border rounded px-2 py-1.5 text-xs bg-white"
      >
        <option value="text">White behind text</option>
        <option value="none">No white background</option>
        <option value="full">White full page</option>
      </select>

      {beforeActions && <div className="border-t border-gray-200 pt-3">{beforeActions}</div>}

      {showActions && <div className={`grid gap-2 border-t border-gray-200 pt-2 ${handleSaveToContentLayout || handlePreviewPreset || showSaveAsNew || showDiscardChanges ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <button
          onClick={handleUpdatePreset}
          disabled={updatingPreset}
          className="bg-gray-900 text-white px-3 py-2 rounded hover:bg-gray-800 disabled:opacity-50 text-xs font-medium"
        >
          {updatingPreset ? 'Saving...' : updateLabel}
        </button>
        {handleSaveToContentLayout && (
          <button
            type="button"
            onClick={handleSaveToContentLayout}
            disabled={updatingPreset}
            className="border border-gray-300 bg-white px-3 py-2 rounded text-xs font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          >
            {updatingPreset ? 'Saving...' : 'Save inside Content Layout'}
          </button>
        )}
        {handlePreviewPreset && (
          <button
            type="button"
            onClick={handlePreviewPreset}
            disabled={updatingPreset}
            className="border border-blue-200 bg-blue-50 px-3 py-2 rounded text-blue-700 hover:bg-blue-100 disabled:opacity-50 text-xs font-medium"
          >
            {previewLabel}
          </button>
        )}
        {showSaveAsNew && (
          <button
            onClick={handleCreateNewPreset}
            disabled={updatingPreset}
            className="border border-gray-300 px-3 py-2 rounded hover:bg-gray-100 disabled:opacity-50 text-xs font-medium"
          >
            Save as New Design Preset
          </button>
        )}
        {showDiscardChanges && (
          <button
            onClick={handleResetPreset}
            disabled={updatingPreset}
            className="border border-gray-300 px-3 py-2 rounded hover:bg-gray-100 disabled:opacity-50 text-xs font-medium"
          >
            Discard Changes
          </button>
        )}
      </div>}
    </div>
  );
}

SliderControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  valueLabel: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

PresetDetailsEditor.propTypes = {
  selectedPreset: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    config: PropTypes.object.isRequired,
  }).isRequired,
  setSelectedPreset: PropTypes.func.isRequired,
  pageLanguage: PropTypes.string,
  handleUpdatePreset: PropTypes.func.isRequired,
  handleSaveToContentLayout: PropTypes.func,
  handlePreviewPreset: PropTypes.func,
  handleCreateNewPreset: PropTypes.func.isRequired,
  handleResetPreset: PropTypes.func.isRequired,
  updatingPreset: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  updateLabel: PropTypes.string,
  showActions: PropTypes.bool,
  showSaveAsNew: PropTypes.bool,
  showDiscardChanges: PropTypes.bool,
  allowTextVisibility: PropTypes.bool,
  textVisibilityOverride: PropTypes.string,
  previewLabel: PropTypes.string,
  beforeActions: PropTypes.node,
};

export default PresetDetailsEditor;
